<script lang="ts">
  import type { Snippet } from "svelte";
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import { auth, logout, refreshAccess } from "$lib/auth";
  import { push } from "svelte-spa-router";
  import { api } from "$lib/api";
  import { canNavigate, canSettingsTab, can, hasFeature, type Feature } from "$lib/permissions";
  import { fetchInbox, LINK_REQUESTS_CHANGED_EVENT } from "$lib/linking";
  import { subscribe, type RealtimeListener } from "$lib/realtime";
  import { companyTypesOf } from "$lib/companyRoles";
  import { filterSurfaces, workspaceOf, type SurfaceId } from "$lib/surfaces";
  import { catalogViewPath, catalogViews } from "$lib/catalogViews";
  import UpgradeModal from "$components/UpgradeModal.svelte";
  import FeedbackWidget from "$components/FeedbackWidget.svelte";

  let {
    tenantSlug,
    active = "",
    pageTitle = "",
    pageSubtitle = "",
    children,
  }: { tenantSlug: string; active?: string; pageTitle?: string; pageSubtitle?: string; children: Snippet } = $props();

  /* Three nav shapes, by width:
       phone    (≤ 700px) — off-canvas drawer behind a hamburger, the way a
                            mobile web app behaves. A 60px icon rail on a
                            360px screen spends a sixth of it on chrome and
                            leaves icons no one can read without labels.
       tablet   (≤ 900px) — the collapsed icon rail: far handier than a drawer
                            when there is room for it beside the content.
       desktop            — the full sidebar. */
  const PHONE_QUERY = "(max-width: 700px)";
  const NARROW_QUERY = "(max-width: 900px)";
  const matches = (q: string) => typeof window !== "undefined" && window.matchMedia(q).matches;

  let collapsed = $state(matches(NARROW_QUERY));
  let isPhone = $state(matches(PHONE_QUERY));
  let drawerOpen = $state(false);

  $effect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) collapsed = true;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  });

  $effect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      isPhone = e.matches;
      // Rotating to landscape mid-navigation must not leave the drawer open
      // over a layout that no longer has one.
      if (!e.matches) drawerOpen = false;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  });

  // In the drawer the labels are the point, so the collapsed rail never applies.
  const railCollapsed = $derived(collapsed && !isPhone);

  /** Close the drawer after any navigation, or it covers where you just went. */
  function closeDrawer() {
    drawerOpen = false;
  }

  $effect(() => {
    if (!drawerOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") drawerOpen = false;
    };
    document.addEventListener("keydown", onEsc);
    // Stop the page behind the drawer scrolling under the finger.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  });
  let fxRate = $state<number | null>(null);
  // Pending inbound requests, split by which partner page they land on so each
  // nav item badges only its own work.
  let pendingByNav = $state<Record<string, number>>({});

  type CompanyType = "manufacturer" | "wholesaler" | "shop";

  type NavItem = {
    id: string;
    /** i18n key for the visible label. Reactive via `$_` so the
     *  sidebar relabels live when the user switches language. */
    labelKey: string;
    icon: string;
    forTypes: CompanyType[];
    /** Company types that see this item disabled with an explanation
     *  instead of not seeing it at all (avoids "where did Prices go?"). */
    advertiseTo?: CompanyType[];
    /** When true, the item is a real link that opens in a new tab. */
    external?: boolean;
    /** The paid feature behind the section (subscription constructor). A
     *  company without it still sees and opens the item — the page shows
     *  the module's banner or lock screen — but the row carries a lock. */
    feature?: Feature;
    /** RolePermission gate: [section, action]. A user whose role lacks it
     *  does not see the item — the API enforces the same matrix, so the nav
     *  must not advertise pages that will 403 (audit OE-A05). */
    permission?: [string, string];
    divider?: boolean;
    /** Drawn only in the short workspace of an invited client: "any" for
     *  both of its variants, "explore" for «возможности системы» alone. */
    workspaceOnly?: "any" | "explore";
  };

  const allNavItems: NavItem[] = [
    // The short workspace (owner, 2026-09-19): the supplier that invited the
    // company, its assortment, and — for «explore» — the page where the rest
    // of the system is laid out and switched on.
    { id: "assortment", labelKey: "nav.assortment", icon: "storefront", forTypes: ["manufacturer", "wholesaler", "shop"], workspaceOnly: "any" },
    { id: "dashboard", labelKey: "nav.dashboard", icon: "dashboard", forTypes: ["manufacturer", "wholesaler", "shop"] },
    // «Мои связи» left the menu on 2026-09-16: the partner map is the last tab
    // of «Мои партнёры», where the partners it draws are kept.
    { id: "search-requests",           labelKey: "nav.partnerSearch",          icon: "travel_explore",  forTypes: ["manufacturer", "wholesaler"], permission: ["link_requests", "view"], feature: "partner_search" },
    // One address book for both ends of the trade (spec 20260811 §6): clients
    // and suppliers are tabs of "Мои партнёры", not separate sidebar entries.
    { id: "partners",                  labelKey: "nav.myPartners",             icon: "groups",         forTypes: ["manufacturer", "wholesaler", "shop"], permission: ["link_requests", "view"] },
    { id: "divider-1",            labelKey: "",                        icon: "",               forTypes: ["manufacturer", "wholesaler", "shop"], divider: true },
    // A shop keeps the full catalogue tools (2026-08-23): where a supplier is
    // not on the platform, the shop enters that supplier's cards itself and
    // tags them with the supplier, so its stock is complete — the supplier
    // simply stays uninvited and no order can be sent there.
    // «Склад» (owner, 2026-09-19) is one section for the one database that
    // Продукты, «Цены и доступ» and Склад used to show three different ways:
    // its four views — Склад, Продукты, Цены, Доступ — are switched under the
    // section title ($lib/catalogViews). The item shows while any view is left
    // to this member and opens the first of them.
    { id: "stock",                       labelKey: "nav.stock",                     icon: "warehouse",        forTypes: ["manufacturer", "wholesaler", "shop"], feature: "warehouse" },
    // The two ledgers under the shelf (2026-08-28): every delivery that
    // reached it, and every invoice that left. Read by whoever does the books.
    { id: "inbound",                     labelKey: "nav.inbound",                   icon: "move_to_inbox",    forTypes: ["manufacturer", "wholesaler", "shop"], permission: ["products", "view"], feature: "warehouse" },
    { id: "outbound",                    labelKey: "nav.outbound",                  icon: "outbox",           forTypes: ["manufacturer", "wholesaler", "shop"], permission: ["orders", "view"], feature: "warehouse" },
    // «Оптовый каталог» parked (2026-08-12 feedback): the propagated supplier
    // cards + the universal Prices form replaced its role. The route stays
    // reachable should the section come back.
    // { id: "wholesale-catalog",    labelKey: "nav.wholesaleCatalog",    icon: "storefront",     forTypes: ["wholesaler"] },
    { id: "divider-2",            labelKey: "",                        icon: "",               forTypes: ["manufacturer", "wholesaler", "shop"], divider: true },
    // One entry for the whole order flow. The draft a buyer is still
    // assembling is not a separate section of the app — it is an order that has
    // not been sent yet, and it now shows as a Draft on the Orders page. A
    // second nav item for it made "compose" and "track" look like two places.
    { id: "orders",               labelKey: "nav.orders",              icon: "receipt_long",   forTypes: ["manufacturer", "wholesaler", "shop"], permission: ["orders", "view"] },
    // Money and the books are their own block under the orders (owner, 2026-09-28).
    { id: "divider-orders",       labelKey: "",                        icon: "",               forTypes: ["manufacturer", "wholesaler", "shop"], divider: true },
    {id:"payments",labelKey:"access.payments",icon:"payments",forTypes:["manufacturer","wholesaler","shop"],permission:["payments","view"]},
    // «Бухгалтерия» — month folders for the accountant — and «Репорт»:
    // excise figures and who owes whom (owner, 2026-09-19).
    { id: "accounting",           labelKey: "nav.accounting",          icon: "account_balance", forTypes: ["manufacturer", "wholesaler", "shop"], permission: ["orders", "view"], feature: "accounting" },
    { id: "reports",              labelKey: "nav.reports",             icon: "assessment",      forTypes: ["manufacturer", "wholesaler", "shop"], permission: ["orders", "view"], feature: "reports" },
    { id: "divider-3",            labelKey: "",                        icon: "",               forTypes: ["manufacturer", "wholesaler", "shop"], divider: true },
    { id: "features", labelKey: "nav.features", icon: "auto_awesome", forTypes: ["manufacturer", "wholesaler", "shop"], workspaceOnly: "explore" },
    // Plans live in Settings → My plan, and nowhere else: the sidebar is for
    // the work, not for what the work costs.
    { id: "public-page",          labelKey: "nav.viewPublicPage",      icon: "public",         forTypes: ["manufacturer", "wholesaler", "shop"], external: true, feature: "public_page" },
  ];

  const tenant = $derived(
    $auth.memberships.find((m) => m.tenant.slug === tenantSlug)?.tenant
  );
  const companyType = $derived<CompanyType>(
    (tenant?.company_type as CompanyType) ?? "manufacturer",
  );
  /* A company can occupy several positions in the chain, and the nav is the
     union of what those positions get: a producer that also distributes needs
     both the catalog tools and the supplier list. */
  const companyTypes = $derived(companyTypesOf(tenant) as CompanyType[]);

  /* Three gates, in this order:
       role adaptation (does this company get the section at all — the
         server-computed surface list, which an operator can override),
       company type (the local fallback, and what "gated" advertising reads),
       user permission (the API enforces the same matrix — audit OE-A05).
     Dividers carry no surface id and survive the first gate; the pass below
     drops any that ended up leading, trailing or doubled. */
  const workspace = $derived(workspaceOf($auth, tenantSlug));
  /** The short workspace keeps only its own items and the modules the
      company switched on; the sections outside any module (payments, the
      books, reports) wait for the full workspace. */
  function inWorkspace(i: NavItem): boolean {
    if (i.workspaceOnly === "any") return !!workspace;
    if (i.workspaceOnly === "explore") return workspace === "explore";
    return !workspace || !["payments", "accounting", "reports"].includes(i.id);
  }
  const navItems = $derived(
    dropStrayDividers(
      filterSurfaces(
        $auth,
        tenantSlug,
        allNavItems.filter(
          (i) =>
            inWorkspace(i) &&
            companyTypes.some(
              (t) => i.forTypes.includes(t) || i.advertiseTo?.includes(t),
            ) &&
            (i.id === "stock"
              ? catalogViews($auth, tenantSlug).length > 0
              : canNavigate($auth, tenantSlug, i.id) &&
                (!i.permission || can($auth, tenantSlug, i.permission[0], i.permission[1] as any))),
        ),
        // «Склад» answers for its four views itself (catalogViews above).
        (i) =>
          i.divider || i.workspaceOnly || ["payments", "accounting", "reports", "stock"].includes(i.id)
            ? null
            : (`nav.${i.id}` as SurfaceId),
      ),
    ),
  );

  /** A divider only means something between two items. */
  function dropStrayDividers(items: NavItem[]): NavItem[] {
    const out: NavItem[] = [];
    for (const item of items) {
      if (item.divider && (out.length === 0 || out[out.length - 1].divider)) continue;
      out.push(item);
    }
    while (out.length && out[out.length - 1].divider) out.pop();
    return out;
  }

  function isGated(item: NavItem): boolean {
    return !companyTypes.some((t) => item.forTypes.includes(t));
  }

  /** Relevant to this company, but its module is not in the subscription. */
  function isPlanGated(item: NavItem): boolean {
    return !!item.feature && !isGated(item) && !hasFeature($auth, tenantSlug, item.feature);
  }

  const membership = $derived(
    $auth.memberships.find((m) => m.tenant.slug === tenantSlug)
  );
  const userRole = $derived(membership?.role ?? "");
  $effect(() => {
    const ent = membership?.entitlements;
    if (ent?.status !== "trialing" || !ent.trial_ends_at) return;
    const delay = Date.parse(ent.trial_ends_at) - Date.now();
    if (delay <= 0 || delay > 2_147_000_000) return;
    const timer = window.setTimeout(() => { void refreshAccess(); }, delay + 100);
    return () => window.clearTimeout(timer);
  });

  async function refreshPendingRequests() {
    try {
      // Every pending invitation badges the one page that now holds them all.
      const inbox = await fetchInbox(tenantSlug, "pending");
      pendingByNav = { partners: inbox.length };
    } catch {
      /* silent fail */
    }
  }

  /* The Partners page announces the pending count after every accept /
     decline / delete — the realtime channel alone left the badge at «1»
     after the request was accepted right there on the page. */
  const onPendingAnnounced = (e: Event) => {
    const pending = (e as CustomEvent).detail?.pending;
    if (typeof pending === "number") pendingByNav = { partners: pending };
    else void refreshPendingRequests();
  };

  onMount(() => {
    let disposed = false;
    const refresh = () => { if (!document.hidden) void refreshAccess(); };
    refresh();
    const accessTimer = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    window.addEventListener(LINK_REQUESTS_CHANGED_EVENT, onPendingAnnounced);
    let realtime: RealtimeListener | null = null;
    (async () => {
      try {
        const rates = await api.get<any[]>("/fx/rates");
        const myRate = rates.find(r => r.currency === tenant?.default_currency);
        if (myRate) fxRate = myRate.rate_to_eur;
      } catch {
        /* silent fail */
      }
      await refreshPendingRequests();
      if (disposed || !tenant?.id || !can($auth, tenantSlug, "link_requests", "view")) return;
      realtime = await subscribe(`tenant:${tenant.id}:link_requests`, (event) => {
        const count = event.data.pending_inbox_count;
        if (typeof count === "number") pendingByNav = { partners: count };
        else refreshPendingRequests();
      });
      if (disposed) realtime.unsubscribe();
    })();
    return () => {
      disposed = true;
      window.clearInterval(accessTimer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener(LINK_REQUESTS_CHANGED_EVENT, onPendingAnnounced);
      realtime?.unsubscribe();
    };
  });

  function go(id: string) {
    closeDrawer();
    if (id === 'account' && !canSettingsTab($auth, tenantSlug, 'company-profile')) id = 'account/personal';
    if (id === "stock") {
      const first = catalogViews($auth, tenantSlug)[0];
      if (first) { push(catalogViewPath(tenantSlug, first)); return; }
    }
    push(`/t/${tenantSlug}/${id}`);
  }

  /** Activate an internal nav item. External items are real <a> links. */
  function activate(item: NavItem) {
    go(item.id);
  }

  function onSimpleKeydown(e: KeyboardEvent, id: string) {
    if (e.key === "Enter" || e.key === " ") go(id);
  }

  async function handleLogout() {
    closeDrawer();
    await logout();
    push("/login");
  }

  function onLogoutKeydown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") handleLogout();
  }

  // Top-bar user menu (avatar dropdown). «Начало работы» stays for everyone:
  // before the wizard is finished it reopens the wizard, afterwards it opens
  // the setup checklist in Settings (spec 20260909 §2.1).
  const onboardingTarget = $derived(
    $auth.user?.onboarding_completed_at ? "account/onboarding" : "onboarding",
  );
  // Platform operators get a way into the Backoffice from wherever they are.
  const showBackoffice = $derived(
    !!$auth.user?.is_staff || !!$auth.user?.is_super_admin,
  );
  let userMenuOpen = $state(false);
  let userMenuEl = $state<HTMLDivElement | null>(null);

  $effect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (userMenuEl && !userMenuEl.contains(e.target as Node)) userMenuOpen = false;
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") userMenuOpen = false;
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  });

  function userMenuGo(id: string) {
    userMenuOpen = false;
    go(id);
  }

</script>

<div class="shell" class:collapsed={railCollapsed} class:phone={isPhone} class:drawer-open={drawerOpen}>
  {#if isPhone && drawerOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="nav-scrim" onclick={closeDrawer}></div>
  {/if}

  <!-- Left nav -->
  <!-- `inert` rather than aria-hidden: a closed drawer is off-screen but its
       links would still be reachable by Tab. -->
  <nav class="nav" inert={isPhone && !drawerOpen}>
    <button
      type="button"
      class="nav-brand"
      onclick={() => (isPhone ? closeDrawer() : (collapsed = !collapsed))}
      aria-label={isPhone ? $_("shell.closeMenu") : $_("shell.toggleNav")}
    >
      {#if railCollapsed}
        <img src="/logo-symbol.svg" alt="OE" class="nav-symbol" />
      {:else}
        <img src="/logo.svg" alt="Order Evolution" class="nav-logo" />
      {/if}
      <span class="material-icons-outlined nav-collapse-icon" aria-hidden="true">
        {isPhone ? "close" : railCollapsed ? "chevron_right" : "chevron_left"}
      </span>
    </button>

    <ul class="nav-list">
      {#each navItems as item}
        {#if item.divider}
          <li class="nav-divider"></li>
        {:else if item.external}
          <li class="nav-item nav-item--link">
            <a
              class="nav-link"
              href={`/#/pub/${tenantSlug}`}
              target="_blank"
              rel="noopener"
            >
              <span class="material-icons-outlined nav-icon" aria-hidden="true">{item.icon}</span>
              {#if !railCollapsed}
                <span class="nav-label">{$_(item.labelKey)}</span>
                {#if isPlanGated(item)}
                  <span class="material-icons-outlined nav-lock" aria-hidden="true" title={$_("billing.nav.locked")}>lock</span>
                {/if}
                <span class="material-icons-outlined nav-external" aria-hidden="true">open_in_new</span>
              {/if}
            </a>
          </li>
        {:else if isGated(item)}
          <li
            class="nav-item nav-item--gated"
            title={$_("nav.gated.manufacturerOnly")}
          >
            <span class="material-icons-outlined nav-icon" aria-hidden="true">{item.icon}</span>
            {#if !railCollapsed}
              <span class="nav-label">{$_(item.labelKey)}</span>
              <span class="material-icons-outlined nav-lock" aria-hidden="true">lock</span>
            {/if}
          </li>
        {:else}
          <li>
            <button
              type="button"
              class="nav-item"
              class:active={active === item.id}
              onclick={() => activate(item)}
              title={isPlanGated(item) ? $_("billing.nav.locked") : undefined}
            >
              <span class="material-icons-outlined nav-icon" aria-hidden="true">{item.icon}</span>
              {#if !railCollapsed}
                <span class="nav-label">
                  {item.id === "cart" && companyType === "wholesaler" ? $_("cart.procurementCart") : $_(item.labelKey)}
                </span>
              {/if}
              {#if pendingByNav[item.id] > 0}
                <span class="nav-badge">{pendingByNav[item.id]}</span>
              {/if}
              {#if !railCollapsed && isPlanGated(item)}
                <span class="material-icons-outlined nav-lock" aria-hidden="true">lock</span>
              {/if}
            </button>
          </li>
        {/if}
      {/each}
    </ul>

    <div class="nav-bottom">
      <!-- FX Display -->
      {#if tenant?.default_currency !== 'EUR'}
        <div class="nav-item nav-item--fx" title="Exchange rate from ECB">
          <span class="material-icons-outlined nav-icon" aria-hidden="true">payments</span>
          {#if !railCollapsed}
            <span class="nav-label">
              {tenant?.default_currency} · 1 EUR = {fxRate ? Number(fxRate).toFixed(4) : '...'}
            </span>
          {/if}
        </div>
      {/if}

      <!-- No language badge here. It sat in the company-wide nav but opened a
           per-user setting, and read as if it were the company's language —
           which is a different field (the public profile's language, in
           Settings → Company profile). The interface language now lives with
           the rest of your personal settings, reachable from the avatar menu
           and from Settings → Personal. -->

      <div
        class="nav-item nav-item--account"
        class:active={active === "account"}
        onclick={() => go("account")}
        onkeydown={(e) => onSimpleKeydown(e, "account")}
        role="button"
        tabindex="0"
      >
        <span class="material-icons-outlined nav-icon" aria-hidden="true">settings</span>
        {#if !railCollapsed}
          <span class="nav-label">
            {$_("shell.settings")}
            <small class="nav-hint">Account & prefs</small>
          </span>
        {/if}
      </div>

      <div
        class="nav-item nav-item--logout"
        onclick={handleLogout}
        onkeydown={onLogoutKeydown}
        role="button"
        tabindex="0"
      >
        <span class="material-icons-outlined nav-icon" aria-hidden="true">logout</span>
        {#if !railCollapsed}<span class="nav-label">{$_("shell.logout")}</span>{/if}
      </div>
    </div>
  </nav>

  <!-- Content area -->
  <div class="main">
    <!-- Top bar -->
    <header class="topbar">
      <button
        type="button"
        class="topbar-burger"
        onclick={() => (drawerOpen = !drawerOpen)}
        aria-label={drawerOpen ? $_("shell.closeMenu") : $_("shell.openMenu")}
        aria-expanded={drawerOpen}
      >
        <span class="material-icons-outlined" aria-hidden="true">menu</span>
      </button>
      <a class="topbar-brand" href={`#/t/${tenantSlug}/dashboard`} aria-label="Order Evolution">
        <img src="/logo-symbol.svg" alt="" />
      </a>
      <div class="topbar-search"></div>
      <div class="topbar-right">
        <!-- Who you are and which company you are acting for. The sidebar
             carries the same four facts, but on a phone the sidebar is behind
             a hamburger and this corner is where the thumb already goes — so
             here it stays visible at every width, and the sidebar copy is the
             one that drops out. -->
        {#if tenant}
          <div class="topbar-context">
            <span class="topbar-line">
              <span class="topbar-name">{$auth.user?.full_name ?? ""}</span>
              {#if userRole}<span class="topbar-role">{$_(`role.${userRole}`, { default: userRole })}</span>{/if}
            </span>
            <span class="topbar-line">
              <span class="topbar-company">{tenant.display_name}</span>
              <span class="topbar-types">
                {companyTypes.map((t) => $_(`profile.type.${t}`)).join(" · ")}
              </span>
            </span>
          </div>
        {/if}
        <div class="user-menu" bind:this={userMenuEl}>
          <button
            type="button"
            class="avatar avatar--btn"
            onclick={() => (userMenuOpen = !userMenuOpen)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            aria-label={$_("shell.userMenu")}
          >
            {($auth.user?.full_name?.trim() || "?")[0].toUpperCase()}
          </button>
          {#if userMenuOpen}
            <div class="user-menu-pop">
              {#if !workspace}
                <button type="button" class="user-menu-item" onclick={() => userMenuGo(onboardingTarget)}>
                  <span class="material-icons-outlined" aria-hidden="true">rocket_launch</span>
                  {$_("shell.menu.onboarding")}
                </button>
              {/if}
              <button type="button" class="user-menu-item" onclick={() => userMenuGo("features")}>
                <span class="material-icons-outlined" aria-hidden="true">auto_awesome</span>
                {$_("feat.title")}
              </button>
              {#if showBackoffice}
                <button
                  type="button"
                  class="user-menu-item"
                 
                  onclick={() => {
                    userMenuOpen = false;
                    push("/backoffice");
                  }}
                >
                  <span class="material-icons-outlined" aria-hidden="true">shield_person</span>
                  {$_("shell.menu.backoffice")}
                </button>
              {/if}
              <!-- Your own details, including the interface language: the
                   Settings → «Личные данные» page (owner, 2026-09-29). -->
              <button type="button" class="user-menu-item" onclick={() => userMenuGo("account/personal")}>
                <span class="material-icons-outlined" aria-hidden="true">person</span>
                {$_("account.personal.title")}
              </button>
              <button type="button" class="user-menu-item" onclick={() => userMenuGo("account")}>
                <span class="material-icons-outlined" aria-hidden="true">settings</span>
                {$_("shell.settings")}
              </button>
              <div class="user-menu-sep"></div>
              <button type="button" class="user-menu-item" onclick={handleLogout}>
                <span class="material-icons-outlined" aria-hidden="true">logout</span>
                {$_("shell.logout")}
              </button>
            </div>
          {/if}
        </div>
      </div>
    </header>

    <!-- Page slot -->
    <div class="content">
      {#if pageTitle}
        <div class="page-header">
          <h1 class="page-title">{pageTitle}</h1>
          {#if pageSubtitle}<p class="page-subtitle">{pageSubtitle}</p>{/if}
        </div>
      {/if}
      {@render children()}
    </div>
  </div>
</div>

<UpgradeModal {tenantSlug} />
<FeedbackWidget {tenantSlug} />

<style>
  /* The page scrolls inside `.content`, not the window (2026-09-16): the
     topbar was already written as sticky but could never stick — `.main` is
     `overflow: hidden`, so a window scroll carried it off the screen — and a
     screen that edits in place needs its filter row, and the «Сохранить
     изменения» in it, to stay put however far the table runs. Fixing the
     height here makes `.content` the scrollport everything sticks against. */
  .shell {
    display: grid;
    grid-template-columns: var(--nav-w) 1fr;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
    background: var(--color-bg);
    transition: grid-template-columns 0.2s ease;
  }
  .shell.collapsed { grid-template-columns: var(--nav-w-collapsed) 1fr; }

  /* ── Nav ── */
  .nav {
    background: var(--color-surface-darker);
    color: var(--color-text-on-dark);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: sticky;
    top: 0;
    height: 100vh;
  }

  .nav-brand {
    height: var(--topbar-h);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 16px;
    cursor: pointer;
    border: none;
    background: #fff;
    border-bottom: 1px solid var(--color-divider);
    flex-shrink: 0;
    width: 100%;
  }
  .shell.collapsed .nav-brand { justify-content: center; padding: 0; }
  .nav-logo {
    height: 36px;
    width: auto;
  }
  .nav-symbol {
    height: 36px;
    width: 38px;
    object-fit: contain;
  }
  .nav-collapse-icon {
    color: var(--color-primary);
    font-size: 22px;
    line-height: 1;
    opacity: 0.9;
    transition: color 0.12s, opacity 0.12s;
  }
  .nav-brand:hover .nav-collapse-icon { color: var(--color-primary-hover); opacity: 1; }
  .shell.collapsed .nav-collapse-icon { display: none; }

  .nav-list {
    list-style: none;
    padding: 12px 0;
    margin: 0;
    flex: 1;
    overflow-y: auto;
    /* `overflow-y: auto` alone computes overflow-x to `auto` as well, so the
       one nav label too long for the rail ("Черновик заказа поставщику") put a
       horizontal scrollbar across the bottom of the sidebar on desktop. Long
       labels clip instead. */
    overflow-x: hidden;
  }

  .nav-divider {
    height: 1px;
    background: rgba(255,255,255,0.07);
    margin: 12px 16px;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    cursor: pointer;
    border-radius: 6px;
    margin: 2px 8px;
    font-size: 14px;
    color: rgba(255,255,255,0.75);
    transition: background 0.12s, color 0.12s;
    white-space: nowrap;
    user-select: none;
    outline: none;
  }
  /* The main nav rows are real <button>s (plain list semantics, no ARIA menu
     roles): strip native button chrome, keep the .nav-item look. The width
     offsets the 8px side margins so the row still fills the rail. */
  button.nav-item { background: none; border: none; font-family: inherit; text-align: left; width: calc(100% - 16px); }
  .nav-item:hover { background: rgba(255,255,255,0.08); color: #fff; }
  .nav-item:focus-visible { box-shadow: 0 0 0 2px var(--color-primary); }
  .nav-item.active {
    background: var(--color-primary);
    color: #fff;
    font-weight: 600;
  }

  .nav-icon {
    flex-shrink: 0;
    width: 20px;
    font-size: 20px;
    line-height: 1;
    text-align: center;
  }

  .nav-label {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
    overflow: hidden;
  }
  .nav-hint {
    font-size: 11px;
    opacity: 0.5;
    font-weight: 400;
  }

  .nav-external {
    margin-left: auto;
    font-size: 14px !important;
    opacity: 0.5;
    flex-shrink: 0;
  }
  .nav-item:hover .nav-external { opacity: 0.85; }

  /* External item rendered as a real link (new tab) */
  .nav-item--link { padding: 0; }
  .nav-item--link .nav-link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    width: 100%;
    color: inherit;
    text-decoration: none;
    border-radius: 6px;
    box-sizing: border-box;
  }

  /* Gated item: visible but locked, tooltip explains why */
  .nav-item--gated {
    cursor: not-allowed;
    opacity: 0.45;
  }
  .nav-item--gated:hover { background: none; color: rgba(255,255,255,0.75); }
  .nav-lock {
    margin-left: auto;
    font-size: 14px !important;
    opacity: 0.7;
    flex-shrink: 0;
  }

  .nav-bottom {
    border-top: 1px solid rgba(255,255,255,0.07);
    padding: 8px 0;
  }

  .nav-item--fx {
    cursor: default;
    opacity: 0.6;
    font-size: 12px;
  }
  .nav-item--fx:hover { background: none; }

  .nav-item--logout { color: rgba(255,100,100,0.85); }
  .nav-item--logout:hover { background: rgba(255,80,80,0.12); color: #ff6666; }

  .nav-badge {
    margin-left: auto;
    background: #e53935;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    flex-shrink: 0;
  }

  /* ── Main ── */
  .main { display: flex; flex-direction: column; min-height: 0; overflow: hidden; }

  .topbar {
    height: var(--topbar-h);
    background: #fff;
    border-bottom: 1px solid var(--color-divider);
    display: flex;
    align-items: center;
    padding: 0 24px;
    gap: 16px;
    position: sticky;
    top: 0;
    /* The topbar makes its own stacking context, so the avatar dropdown can
       never escape it — this value has to clear every in-page sticky header
       (highest is 200) while staying under modal backdrops (1000+). */
    z-index: 500;
    flex-shrink: 0;
  }
  .topbar-search { flex: 1; }

  .topbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: var(--color-text-muted);
  }

  .avatar {
    width: 34px; height: 34px;
    border-radius: 50%;
    background: var(--color-primary);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 600;
    flex-shrink: 0;
  }
  .avatar--btn {
    border: none; cursor: pointer; padding: 0; font-family: inherit;
  }
  .avatar--btn:hover { box-shadow: 0 0 0 3px rgba(33,133,213,0.25); }

  .user-menu { position: relative; }
  .user-menu-pop {
    position: absolute; top: calc(100% + 8px); right: 0; z-index: 900;
    min-width: 210px;
    background: #fff;
    border: 1px solid var(--color-divider);
    border-radius: var(--radius);
    box-shadow: 0 8px 28px rgba(0,0,0,0.14);
    padding: 6px;
    display: flex; flex-direction: column;
  }
  .user-menu-item {
    display: flex; align-items: center; gap: 10px;
    border: none; background: none; cursor: pointer;
    padding: 9px 12px; border-radius: var(--radius);
    font-family: inherit; font-size: 13.5px; font-weight: 500;
    color: var(--color-text); text-align: left;
  }
  .user-menu-item:hover { background: var(--color-bg); }
  .user-menu-item .material-icons-outlined { font-size: 18px; color: var(--color-text-muted); }
  .user-menu-sep { height: 1px; background: var(--color-divider); margin: 4px 6px; }

  /* No top padding: a sticky header sticks at the scrollport's padding edge,
     so a padded top left a strip above every sticky header (the product
     card's title and anchor rail) through which scrolled fields showed. The
     spacer gives the same 24px at rest and scrolls away with the page. */
  .content { flex: 1; min-height: 0; padding: 0 24px 24px; overflow-y: auto; }
  .content::before { content: ""; display: block; height: 24px; }

  .page-header { margin-bottom: 20px; }
  .page-title { font-size: 22px; font-weight: 700; color: var(--color-text); margin: 0 0 4px; }
  .page-subtitle { font-size: 13px; color: var(--color-text-muted); margin: 0; }

  /* Identity block: two lines, each "what" followed by its qualifier —
     name / role above, company / type below. */
  .topbar-context {
    display: flex; flex-direction: column; gap: 1px;
    line-height: 1.25; text-align: right; min-width: 0;
  }
  .topbar-line { display: flex; align-items: baseline; justify-content: flex-end; gap: 6px; min-width: 0; }
  .topbar-name { font-size: 12px; color: var(--color-text); font-weight: 600; }
  .topbar-company { font-size: 12px; color: var(--color-text); font-weight: 600; }
  .topbar-name, .topbar-company {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 22ch;
  }
  .topbar-role, .topbar-types { font-size: 10px; color: var(--color-text-muted); white-space: nowrap; }

  /* ── Phone: off-canvas drawer ─────────────────────────────────────────
     The hamburger and the brand mark only exist below 700px; above it the
     sidebar carries both, and the topbar stays as it was. */
  .topbar-burger,
  .topbar-brand { display: none; }

  .shell.phone { grid-template-columns: 1fr; }

  .shell.phone .nav {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    width: min(288px, 84vw);
    height: 100dvh;
    z-index: 1100;
    transform: translateX(-100%);
    transition: transform 0.22s ease;
    box-shadow: 0 0 32px rgba(0,0,0,0.35);
  }
  .shell.phone.drawer-open .nav { transform: none; }

  .nav-scrim {
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: rgba(0,0,0,0.45);
  }

  @media (max-width: 700px) {
    .topbar-burger {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px; height: 40px;
      margin-left: -8px;
      border: none;
      background: none;
      border-radius: 8px;
      color: var(--color-surface-darker);
      cursor: pointer;
    }
    .topbar-burger:active { background: var(--color-bg); }
    .topbar-burger .material-icons-outlined { font-size: 26px; }

    .topbar-brand { display: inline-flex; align-items: center; }
    .topbar-brand img { height: 30px; width: auto; }

    .topbar { padding: 0 12px; gap: 8px; }
    /* This corner keeps the active user and company visible on a phone. */
    .topbar-name, .topbar-company { max-width: 26vw; font-size: 11px; }
    .topbar-role, .topbar-types { font-size: 9px; }
    .topbar-types { overflow: hidden; text-overflow: ellipsis; max-width: 22vw; }

    .content { padding: 0 12px 16px; }
    .content::before { height: 16px; }
  }
</style>
